import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { Button } from "../ui/button"; // Assuming you have a custom Button
import { Check, Star } from 'lucide-react'; // Using lucide-react icons
import { STRIPE_PRICES } from '../../constants'; // Assuming STRIPE_PRICES are defined here

// Define standard button styles with whitespace-nowrap
const PURPLE_BUTTON_STYLE = 'bg-purple-700 hover:bg-purple-800 text-white dark:bg-purple-700 dark:hover:bg-purple-800 font-semibold py-2.5 px-5 rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-75 w-full text-sm sm:text-base transition-colors duration-150 ease-in-out whitespace-nowrap';
const GREEN_BUTTON_STYLE = 'bg-green-600 hover:bg-green-700 text-white dark:bg-green-600 dark:hover:bg-green-700 font-semibold py-2.5 px-5 rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-75 w-full text-sm sm:text-base transition-colors duration-150 ease-in-out whitespace-nowrap';

const DEFAULT_PLANS_CONFIG = [
  {
    id: 'basic',
    name: 'Basic Plan',
    price: '£25',
    priceDisplay: '£25',
    priceSuffix: '/year',
    monthlyEquivalent: '£2.08 / month',
    trialInfoDefault: 'Includes 60-day free trial',
    features: [
      { title: 'Unlimited Invoices', description: 'Create and manage unlimited invoices for all your dental practices' },
      { title: 'Monthly Invoicing', description: 'Generate and send invoices on a monthly cycle' },
      { title: 'PDF Generation', description: 'Professional PDF invoices with your details auto-filled' },
      { title: 'Practice Management', description: 'Manage multiple dental practices with ease' },
      { title: 'Secure Data Storage', description: 'Bank-level encryption for all your sensitive information' },
      { title: 'Basic Support', description: 'Email support within 24 hours' },
    ],
  },
  {
    id: 'pro',
    name: 'Pro Plan',
    price: '£50',
    priceDisplay: '£50',
    priceSuffix: '/year',
    monthlyEquivalent: '£4.17 / month',
    trialInfoDefault: 'Includes 60-day free trial', // Default, will be overridden if trial used
    features: [
      { title: 'Everything in Basic', description: 'All features from the Basic plan included' },
      { title: 'Daily Work Log', description: 'Track daily work, earnings, and deductions efficiently' },
      { title: 'Revenue Reports', description: 'Detailed financial analytics and income tracking' },
      { title: 'Invoice History', description: 'Complete searchable history of all invoices' },
      { title: 'Expense Management', description: 'Track expenses and mileage to maximize deductions' },
      { title: 'Tax Estimations', description: 'Automated tax liability estimations based on income & expenses' },
      { title: 'Priority Support', description: 'Premium support with faster response times' },
    ],
    bestValue: true,
    bestValueText: 'BEST VALUE',
    isBestValue: true
  }
];

const StripeCheckout = ({ 
  loading, 
  availedTrials = {}, 
  onSelectPlan, 
  plansConfig = DEFAULT_PLANS_CONFIG,
  customButtonTexts = null, // New prop for custom button texts
  planLoading // New prop to indicate loading state for specific plan
}) => {
  // Add auth reference
  const auth = getAuth();
  
  // Add an effect to validate authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      // Check if we have a user
      if (auth.currentUser) {
        try {
          // Force a token refresh to ensure we have a valid token
          // for future API calls
          await auth.currentUser.getIdToken(true);
          if (process.env.NODE_ENV !== 'production') {
            console.log('[StripeCheckout] Authentication token refreshed on component mount');
          }
        } catch (error) {
          if (process.env.NODE_ENV !== 'production') {
            console.error('[StripeCheckout] Failed to refresh authentication token:', error);
          }
        }
      } else {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[StripeCheckout] No authenticated user found');
        }
      }
    };
    
    checkAuth();
  }, [auth]);

  const CheckIcon = () => (
    <svg className="w-5 h-5 text-green-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
    </svg>
  );

  const StarIcon = () => (
    <svg className="w-5 h-5 text-yellow-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.97a1 1 0 00.95.69h4.18c.969 0 1.371 1.24.588 1.81l-3.387 2.458a1 1 0 00-.364 1.118l1.287 3.97c.3.921-.755 1.688-1.54 1.118l-3.387-2.458a1 1 0 00-1.175 0l-3.387 2.458c-.784.57-1.838-.197-1.539-1.118l1.287-3.97a1 1 0 00-.364-1.118L2.04 9.397c-.783-.57-.38-1.81.588-1.81h4.18a1 1 0 00.95-.69l1.286-3.97z"></path>
    </svg>
  );

  const anyTrialAvailedOverall = Object.values(availedTrials).some(availed => availed === true);
  if (process.env.NODE_ENV !== 'production') {
    console.log("[StripeCheckout] Initial render/re-render. anyTrialAvailedOverall:", anyTrialAvailedOverall, "availedTrials:", availedTrials);
  }


  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
        {plansConfig.map((plan) => {
          if (!plan || !plan.id) {
            if (process.env.NODE_ENV !== 'production') {
              console.error("[StripeCheckout] Warning: Plan object or plan.id is undefined in plansConfig map. Plan:", plan);
            }
            // Optionally, skip rendering this card or render an error state for it
            // return null; 
          }
          const trialAvailedForThisSpecificPlan = availedTrials[plan.id] === true;
          
          let buttonText;
          let currentButtonClass;
          let actionHandler;
          let currentTrialInfoText = plan.trialInfoDefault;
          let showPaymentNote = false;

          if (anyTrialAvailedOverall) {
            // Use custom button text if provided
            buttonText = customButtonTexts?.[plan.id] || 
                        `Subscribe to ${plan.id ? plan.id.charAt(0).toUpperCase() + plan.id.slice(1) : 'Plan'}`;
            currentButtonClass = PURPLE_BUTTON_STYLE;
            actionHandler = () => {
              if (process.env.NODE_ENV !== 'production') {
                console.log(`[StripeCheckout] Clicked plan: ${plan.id}, anyTrialAvailedOverall: ${anyTrialAvailedOverall}. Intending to call onSelectPlan with isTrial: false.`);
                console.log("[StripeCheckout] onSelectPlan function reference:", onSelectPlan);
              }
              onSelectPlan(plan.id, false); // false for isTrial
            };
            currentTrialInfoText = trialAvailedForThisSpecificPlan ? "Trial previously used for this plan" : "Trial option not available";
            showPaymentNote = true;
          } else {
            // Use custom button text if provided
            buttonText = customButtonTexts?.[plan.id] || 
                        `Start ${plan.id === 'basic' ? 'Free' : (plan.name ? plan.name.split(' ')[0] : 'Plan')} Trial`;
            currentButtonClass = plan.id === 'pro' ? GREEN_BUTTON_STYLE : PURPLE_BUTTON_STYLE;
            actionHandler = () => {
              if (process.env.NODE_ENV !== 'production') {
                console.log(`[StripeCheckout] Clicked plan: ${plan.id}, anyTrialAvailedOverall: ${anyTrialAvailedOverall}. Intending to call onSelectPlan with isTrial: true.`);
                console.log("[StripeCheckout] onSelectPlan function reference:", onSelectPlan);
              }
              onSelectPlan(plan.id, true); // true for isTrial
            };
            currentTrialInfoText = plan.trialInfoDefault;
            showPaymentNote = false;
          }

          return (
            <div 
              key={plan.id || Math.random()} // Fallback key if plan.id is undefined
              className={`
                app-card-bg p-6 rounded-lg shadow-lg flex flex-col
                ${plan.isBestValue ? 'border-2 app-border-accent' : 'app-border'}
                relative
              `} // Use app-card-bg and app-border
            >
              {plan.isBestValue && (
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <span className="px-3 py-1 text-xs font-semibold text-white bg-green-500 rounded-full dark:text-green-950">
                    {plan.bestValueText}
                  </span>
                </div>
              )}
              <h3 className="text-2xl font-bold app-text-accent mb-1">{plan.name}</h3> {/* app-text-accent */}
              <div className="mb-1">
                <span className="text-4xl font-extrabold app-text-primary">{plan.price}</span> {/* app-text-primary */}
                <span className="text-sm app-text-secondary">{plan.priceSuffix}</span> {/* app-text-secondary */}
              </div>
              <p className="text-sm app-text-secondary mb-1">{plan.monthlyEquivalent}</p> {/* app-text-secondary */}
              
              {/* UPDATED: Better color contrast for trial status message that works across themes */}
              <p className={`text-sm mt-4 font-medium ${trialAvailedForThisSpecificPlan || (anyTrialAvailedOverall && plan.id === 'pro') 
                ? 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 p-1 rounded' 
                : 'text-gray-500 dark:text-gray-400'}`}>
                {anyTrialAvailedOverall ? 
                  (trialAvailedForThisSpecificPlan ? "✓ Trial already used for this plan" : "Trial option no longer available") 
                  : currentTrialInfoText}
              </p>

              <ul className="space-y-2 mb-6 flex-grow">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    {trialAvailedForThisSpecificPlan && plan.id === 'pro' ? <StarIcon /> : <CheckIcon />}
                    <div>
                      <span className="font-semibold app-text-primary">{feature.title}</span> {/* app-text-primary */}
                      <p className="text-xs app-text-secondary">{feature.description}</p> {/* app-text-secondary */}
                    </div>
                  </li>
                ))}
              </ul>
              
              {/* FIXED: Add fixed height container to ensure buttons align horizontally */}
              <div className="flex flex-col h-[80px] justify-end">
                <Button
                  onClick={actionHandler}
                  className={`w-full py-3 text-lg font-semibold rounded-md !text-white ${currentButtonClass} ${loading || planLoading === plan.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={loading || planLoading === plan.id}
                >
                  {loading || planLoading === plan.id ? (
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {/* Show correct spinner text based on flow */}
                      {anyTrialAvailedOverall
                        ? "Redirecting to Payment..."
                        : "Starting Trial..."}
                    </div>
                  ) : buttonText}
                </Button>
                
                {/* Additional text below buttons */}
                <div className="mt-2 h-5 text-center"> {/* Fixed height container for notes */}
                  {plan.id === 'pro' && (
                    <p className="text-xs app-text-secondary">
                      {showPaymentNote
                        ? "Payment method required for subscription."
                        : "No credit card required for trial."}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StripeCheckout;
